import React, { useEffect, useState } from "react";
import { LogOut, Mail, Phone, MapPin, Truck, Package } from "lucide-react";
import { toast } from "sonner";
import { apiClient, getApiErrorMessage } from "../../lib/apiClient";
import { Skeleton } from "../../components/ui/skeleton";
import { Textarea } from "../../components/ui/textarea";
import { Button } from "../../components/ui/button";
import { ErrorState } from "../../components/shared/ErrorState";
import { useAuth } from "../../context/AuthContext";
import { formatDate, initials } from "../../lib/utils";

function InstructionsCard({ me, onSaved }) {
  const [delivery, setDelivery] = useState(me.delivery_notes || "");
  const [packing, setPacking] = useState(me.packaging_notes || "");
  const [saving, setSaving] = useState(false);

  const dirty = (delivery || "") !== (me.delivery_notes || "") || (packing || "") !== (me.packaging_notes || "");

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await apiClient.patch("/customer/subscription/notes", {
        delivery_notes: delivery,
        packaging_notes: packing,
      });
      toast.success("Instructions saved");
      onSaved(data);
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Could not save your instructions."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-2xl border border-border bg-surface p-4 shadow-sm" data-testid="customer-instructions-card">
      <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-text-muted">My Instructions</p>

      <label className="mb-1.5 flex items-center gap-2 text-sm font-medium text-text-main">
        <Truck size={15} strokeWidth={1.5} className="text-primary" /> Delivery instructions
      </label>
      <Textarea
        value={delivery}
        onChange={(e) => setDelivery(e.target.value)}
        maxLength={255}
        rows={3}
        placeholder="e.g. Leave at the front door, ring the bell twice."
        data-testid="customer-delivery-notes-input"
      />

      <label className="mb-1.5 mt-4 flex items-center gap-2 text-sm font-medium text-text-main">
        <Package size={15} strokeWidth={1.5} className="text-primary" /> Packing instructions
      </label>
      <Textarea
        value={packing}
        onChange={(e) => setPacking(e.target.value)}
        maxLength={500}
        rows={3}
        placeholder="e.g. Extra spicy, no onions, pack roti separately."
        data-testid="customer-packing-notes-input"
      />

      <Button
        className="mt-4 w-full"
        onClick={save}
        disabled={!dirty || saving}
        data-testid="customer-save-instructions-button"
      >
        {saving ? "Saving…" : "Save Instructions"}
      </Button>
    </div>
  );
}

export default function CustomerProfile() {
  const { logout } = useAuth();
  const [state, setState] = useState({ loading: true, error: null, me: null });

  const load = () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    apiClient
      .get("/customer/me")
      .then(({ data }) => setState({ loading: false, error: null, me: data }))
      .catch((error) => setState({ loading: false, error: getApiErrorMessage(error, "Could not load your profile."), me: null }));
  };

  useEffect(() => { load(); }, []);

  if (state.loading) {
    return (
      <div data-testid="customer-profile-page">
        <Skeleton className="h-20 w-full rounded-2xl" />
        <Skeleton className="mt-3 h-40 w-full rounded-2xl" />
        <Skeleton className="mt-3 h-32 w-full rounded-2xl" />
      </div>
    );
  }

  if (state.error) {
    return <ErrorState message={state.error} onRetry={load} testId="customer-profile-error" />;
  }

  const me = state.me;
  const fullName = [me.firstname, me.lastname].filter(Boolean).join(" ") || "Customer";
  const addressLines = [me.address, [me.city, me.state, me.postal_code].filter(Boolean).join(", ")].filter(Boolean);

  return (
    <div className="space-y-4" data-testid="customer-profile-page">
      <div className="flex items-center gap-3 rounded-2xl border border-border bg-surface p-4 shadow-sm" data-testid="customer-profile-header">
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary font-heading text-lg font-bold text-accent">
          {initials(fullName)}
        </div>
        <div>
          <p className="text-base font-semibold text-text-main">{fullName}</p>
          <p className="text-xs text-text-muted">Customer ID #{me.customer_id}</p>
        </div>
      </div>

      <div className="rounded-2xl border border-border bg-surface p-4 shadow-sm" data-testid="customer-profile-info-card">
        <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-text-muted">Contact & Address</p>
        <div className="space-y-3 text-sm text-text-main">
          <p className="flex items-center gap-2.5"><Mail size={15} strokeWidth={1.5} className="shrink-0 text-text-muted" /> {me.email || "—"}</p>
          <p className="flex items-center gap-2.5"><Phone size={15} strokeWidth={1.5} className="shrink-0 text-text-muted" /> {me.phone || "—"}</p>
          {addressLines.length > 0 && (
            <p className="flex items-start gap-2.5">
              <MapPin size={15} strokeWidth={1.5} className="mt-0.5 shrink-0 text-text-muted" />
              <span>{addressLines.map((l, i) => <span key={i} className="block">{l}</span>)}</span>
            </p>
          )}
        </div>
      </div>

      {me.has_subscription && (
        <div className="rounded-2xl border border-border bg-surface p-4 shadow-sm" data-testid="customer-profile-subscription-card">
          <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-text-muted">Membership</p>
          <div className="space-y-2 text-sm">
            <div className="flex items-center justify-between"><span className="text-text-muted">Plan</span><span className="font-medium text-text-main">{me.plan_name}</span></div>
            <div className="flex items-center justify-between"><span className="text-text-muted">Type</span><span className="font-medium text-text-main">{me.type_label}</span></div>
            <div className="flex items-center justify-between"><span className="text-text-muted">Start date</span><span className="font-medium text-text-main">{formatDate(me.start_date)}</span></div>
            <div className="flex items-center justify-between"><span className="text-text-muted">Renewal date</span><span className="font-medium text-text-main">{formatDate(me.end_date)}</span></div>
          </div>
        </div>
      )}

      {me.has_subscription && (
        <InstructionsCard me={me} onSaved={(data) => setState((s) => ({ ...s, me: data }))} />
      )}

      <button
        type="button" onClick={logout}
        className="flex w-full items-center justify-center gap-2 rounded-xl border border-danger/30 bg-danger-bg py-3.5 text-sm font-medium text-danger transition-all active:scale-[0.98]"
        data-testid="customer-profile-logout-button"
      >
        <LogOut size={16} strokeWidth={1.5} /> Logout of Account
      </button>
    </div>
  );
}
